/**
 * FieldTreeItem Component
 *
 * Individual field item in the tree view.
 * Shows label, description, type badge, and selection state.
 */

import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { FieldTreeItemProps } from './types';

// Type badges for visual indication — labelKey resolved at render time via t()
const TYPE_BADGES: Record<
  string,
  { labelKey: string; defaultLabel: string; className: string }
> = {
  number: {
    labelKey: 'fieldPicker.types.number',
    defaultLabel: 'Number',
    className: 'bg-primary-dim text-primary-fg',
  },
  text: {
    labelKey: 'fieldPicker.types.text',
    defaultLabel: 'Text',
    className: 'bg-success-dim text-success-fg',
  },
  boolean: {
    labelKey: 'fieldPicker.types.boolean',
    defaultLabel: 'Yes/No',
    className: 'bg-info-dim text-info-fg',
  },
  select: {
    labelKey: 'fieldPicker.types.select',
    defaultLabel: 'Select',
    className: 'bg-warning-dim text-warning-fg',
  },
  date: {
    labelKey: 'fieldPicker.types.date',
    defaultLabel: 'Date',
    className: 'bg-primary-dim text-primary-fg',
  },
};

export const FieldTreeItem = forwardRef<HTMLButtonElement, FieldTreeItemProps>(
  function FieldTreeItem({ field, isSelected, isFocused, onClick }, ref) {
    const { t } = useTranslation('common');
    const typeBadge = field.inputType ? TYPE_BADGES[field.inputType] : TYPE_BADGES.text;

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={field.disabled}
        className={cn(
          'w-full flex flex-col items-start gap-0.5 px-3 py-2 text-left',
          'rounded transition-colors cursor-pointer',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          // Default state
          'hover:bg-bg-muted',
          // Selected state
          isSelected && 'bg-primary-dim border-l-2 border-primary',
          // Focused state (keyboard nav)
          isFocused && !isSelected && 'bg-bg-subtle ring-1 ring-primary/50',
          // Combined selected + focused
          isSelected && isFocused && 'ring-1 ring-primary',
          // Disabled state
          field.disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent'
        )}
        role="option"
        aria-selected={isSelected}
        data-focused={isFocused}
        data-field-name={field.name}
      >
        <div className="flex items-center gap-2 w-full">
          {field.icon && (
            <span className="flex-shrink-0 text-fg-muted">{field.icon}</span>
          )}
          <span
            className={cn(
              'flex-1 text-sm font-medium truncate',
              isSelected ? 'text-primary-fg' : 'text-fg'
            )}
          >
            {field.label}
          </span>
          {typeBadge && (
            <span
              className={cn(
                'flex-shrink-0 px-1.5 py-0.5 text-xs rounded',
                typeBadge.className
              )}
            >
              {t(typeBadge.labelKey, typeBadge.defaultLabel)}
            </span>
          )}
        </div>
        {field.description && (
          <span className="text-xs text-fg-muted w-full pl-0 line-clamp-2">
            {field.description}
          </span>
        )}
      </button>
    );
  }
);

export default FieldTreeItem;
